export * from './chat';
export * from './fetch';
export { queryStoreMarketingVisible } from './storeMarketing';
import { ChartData, RecentQuestionsResponse } from '@/types/services';
import { http, HttpError } from './fetch';
import { API_BASE_URL } from '@/common/api';
import { common, getIdentityUid, getWebFetchMode, userInfo } from '@/common';
import { isMindSource } from '@/utils/mindHost';
import { createSSEEventSource, SSEEventHandlers } from '@/utils/sse';
import { ConversationSessionsResponse, IProgressHistoryItem, TAnalysisStatus, SolutionAgentRole } from '@/types';
import {
  delay,
  MOCK_RECENT_QUESTIONS,
  MOCK_RECOMMEND_QUESTIONS,
  MOCK_SESSION,
  MOCK_RECORD_CHART,
  MOCK_SQL_DATA,
  MOCK_SUMMARIZE,
  maybeFailMock,
  nextMockScenario,
  MOCK_REPORT,
  MOCK_HISTORY_SESSIONS,
} from './MOCK';

const baseURL = API_BASE_URL;
const _path = '';

console.log('----api----', baseURL);

/** 是否启用 mock（开发阶段） */
let USE_MOCK = false;
export const USE_MOCK_SWITCH = (v: boolean) => {
  console.info(`[MOCK] USE_MOCK set to【${v}】`);
  USE_MOCK = v;
}

/** 公共请求头。init 下发 source 后带上 Header-Magicai-Source。 */
const commonHeaders = (): Record<string, string> => {
  const source = String(common.source ?? '').trim();
  return {
    'x-token': `${common.token}`,
    'x-language': common.language ?? '',
    ...(source ? { 'Header-Magicai-Source': source } : {}),
  };
};

/** init 的解决方案编码 + 当前选中角色。除 solution_agents 和历史列表外，业务请求都带这两项。 */
function solutionRoleFields(): { solution_code: string; agent_role_code: string } {
  return {
    solution_code: String(common.solution_code ?? ''),
    agent_role_code: common.agentRoleCode ?? '',
  };
}

/** 老字段垫底，init.extraSession 同名覆盖。Mind 的 web_fetch 以输入框开关为准，盖过 init。 */
function buildExtraSession(): Record<string, unknown> {
  return {
    org_id: common.orgId,
    store_id: common.storeId,
    ...(common.extraSession ?? {}),
    ...(isMindSource(common.source) ? { web_fetch: getWebFetchMode() } : {}),
  };
}

/**
 * 获取历史对话列表（仅 channel=agent）。
 * 503（PG 未配置）视为无数据。
 */
export const getConversationSessions = async (
  page = 1,
  pageSize = 20,
): Promise<ConversationSessionsResponse> => {
  const uid = getIdentityUid();
  const empty: ConversationSessionsResponse = { total: 0, page, page_size: pageSize, items: [] };
  if (!uid) return empty;

  if (USE_MOCK) {
    await delay(200);
    const all = MOCK_HISTORY_SESSIONS.map((session) => ({
      uid,
      channel: 'agent' as const,
      tenant_code: common.tenantCode ?? '',
      solution_code: String(common.solution_code ?? ''),
      biz_type: common.bizType ?? '',
      session_key: session.id,
      updated_at: new Date(session.updatedAt).toISOString(),
      turns: [
        {
          turn_key: `${session.id}-turn`,
          question: session.title,
          answer: '',
          created_at: new Date(session.createdAt).toISOString(),
        },
      ],
    }));
    const start = (page - 1) * pageSize;
    return {
      total: all.length,
      page,
      page_size: pageSize,
      items: all.slice(start, start + pageSize),
    };
  }

  const params: Record<string, string | number> = {
    uid,
    page,
    page_size: pageSize,
    channel: 'agent',
  };
  if (common.tenantCode) params.tenant_code = common.tenantCode;
  if (common.solution_code) params.solution_code = String(common.solution_code);
  if (common.bizType) params.biz_type = common.bizType;
  if (common.storeId?.trim()) {
    params.store_id = common.storeId.trim();
  } else if (common.orgId?.trim()) {
    params.org_id = common.orgId.trim();
  }

  try {
    return await http.get<ConversationSessionsResponse>(`${baseURL}/conversation/sessions${_path}`, {
      params,
      headers: commonHeaders(),
    });
  } catch (err) {
    if (err instanceof HttpError && err.status === 503) {
      return empty;
    }
    throw err;
  }
};

/**
 * 获取当前 solution 下可用的角色列表。
 * GET /api/v1/solution_agents?tenant_code=...&biz_type=...&solution_code=...
 */
export const getSolutionAgents = async (solutionCode: string): Promise<SolutionAgentRole[]> => {
  return http.get(`${baseURL}/solution_agents${_path}`, {
    params: {
      tenant_code: common.tenantCode,
      biz_type: common.bizType,
      solution_code: solutionCode,
      lang: common.language,
    },
    headers: commonHeaders(),
  });
};

/**
 * 获取推荐问题，首次对话前调用
 */
export const recentQuestions = async (): Promise<{ questions: string[] }> => {
  if (USE_MOCK) {
    await delay(600);
    return MOCK_RECENT_QUESTIONS;
  }
  return http.post(
    `${baseURL}/chat/recent_questions${_path}`,
    { tenant_code: common.tenantCode, ...solutionRoleFields(), biz_code: common.bizType, ...userInfo },
    { headers: commonHeaders() },
  );
};

/**
 * 根据对话记录 ID 获取推荐追问（chat_record_id 来自 sessionContinue 返回的 record_id）
 */
export const recommendQuestions = async (
  chat_record_id: number,
  token: string,
  signal?: AbortSignal,
): Promise<string[]> => {
  if (USE_MOCK) {
    await delay(800, signal);
    return MOCK_RECOMMEND_QUESTIONS;
  }
  return http.post(
    `${baseURL}/chat/recommend_questions${_path}/${chat_record_id}`,
    { chat_record_id, biz_code: common.bizType, q_token: token, ...userInfo },
    { headers: commonHeaders(), signal },
  );
};

/**
 * 开启新会话，返回 access_token 和 chat_id
 */
export const sessionStart = async (): Promise<{ access_token: string; chat_id: number }> => {
  if (USE_MOCK) {
    await delay(400);
    return MOCK_SESSION;
  }
  return http.post(
    `${baseURL}/session/start${_path}`,
    {
      tenant_code: common.tenantCode,
      ...solutionRoleFields(),
      biz_code: common.bizType,
      org_id: common.orgId,
      store_id: common.storeId,
      ...userInfo,
      uid: getIdentityUid(),
    },
    { headers: commonHeaders() },
  );
};

/**
 * 经营分析 - 初始化会话 (BOSS_REPORT)
 * POST /api/v1/agent/start
 * 同步初始化 claw 会话；BFF 固定下游 userPrompt 为 do not replay, just init，勿在请求体传 userPrompt。
 */
export const agentStart = async (): Promise<{ session_id: string; request_id: string }> => {
  if (USE_MOCK) {
    await delay(400);
    return {
      session_id: 'a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6',
      request_id: 'req_' + Date.now(),
    };
  }
  return http.post(`${baseURL}/agent/start${_path}`, {
    tenant_code: common.tenantCode,
    ...solutionRoleFields(),
    biz_code: common.bizType,
    uid: getIdentityUid(),
    payload: {
      userPrompt: "初始化会话",
      extraSession: buildExtraSession(),
    },
  },
    { headers: commonHeaders() },
  );
};

/**
 * 已有会话继续提问（非流式）
 *
 * @param accessToken - sessionStart 返回的 access_token
 * @param chatId      - sessionStart 返回的 chat_id
 * @param question    - 用户提问内容
 *
 * @stream-reserved
 * 第四个参数 `stream` 目前固定传 false（非流式）。
 * 后续接入 SSE 流式时：
 *   1. 将 stream 改为 true
 *   2. 使用 fetch 原生 ReadableStream 替换 http.post 处理响应
 *   3. 在 ChatPanel.handleSend 中逐 chunk 调用 updateMessage 渲染增量内容
 */
export const sessionContinue = async (
  accessToken: string,
  chatId: string,
  question: string,
  // @stream-reserved: 流式时改为 true，配合 SSE 处理
  stream = false,
  signal?: AbortSignal,
): Promise<RecentQuestionsResponse> => {
  if (USE_MOCK) {
    await delay(1500, signal);
    maybeFailMock();
    const scenario = nextMockScenario();
    return { ...scenario, record_id: Date.now(), title: question.slice(0, 20) };
  }
  return http.post(
    `${baseURL}/session/question${_path}`,
    {
      token: accessToken,
      chat_id: chatId,
      question,
      stream,
      lang: common.language,
      biz_code: common.bizType,
      ...userInfo,
      uid: getIdentityUid(),
    },
    { headers: commonHeaders(), signal },
  );
};
/**
 * 记录反馈
 * @param accessToken
 * @param chatRecordId sessionStart 返回的 chat_record_id
 * @param comment 用户反馈内容
 * @param comment
 * @returns
 */
export const feedbackQuestion = ({ accessToken, chatRecordId, comment }: {
  accessToken?: string;
  chatRecordId?: string | number;
  comment: string,
}) => {
  return http.post(
    `${baseURL}/chat/feedback${_path}`,
    {
      token: accessToken,
      chat_record_id: chatRecordId,
      feedback: comment,
      biz_code: common.bizType,
      ...userInfo,
    },
    { headers: commonHeaders() },
  );
};

/**
 * 经营分析师角色中的「分析报告」反馈接口
 * 要和上面的接口保持一致，现在没有办法只能分两个操蛋的接口处理
 * @returns 
 */
export const agentFeedbackquestion = ({ feedback, sessionId, turnId }: {
  feedback: "good" | "bad";
  sessionId?: string;
  turnId?: string;
}) => {
  return http.post(
    `${baseURL}/agent/feedback${_path}`,
    {
      feedback,
      sessionId,
      turnId,
      ...solutionRoleFields(),
      tenant_code: common.tenantCode,
      biz_code: common.bizType,
      store_id: common.storeId,
    },
    { headers: commonHeaders() },
  );
}

/**
 * 推荐提问，还没开始对话的时候
 */
export const iceBreaker = async (): Promise<{ questions: string[] }> => {
  return http.post(
    `${baseURL}/chat/ice_breaker${_path}`,
    {
      tenant_code: common.tenantCode,
      ...solutionRoleFields(),
      biz_code: common.bizType,
      language: common.language,
      ...userInfo,
    },
    { headers: commonHeaders() },
  );
};

/**
 * 分段式请求，对话中提数，不包含图表，与recordChat配合使用
 * @returns
 */
export const sqlDataChat = async (
  chatId: string,
  question: string,
  token: string,
  signal?: AbortSignal,
): Promise<{
  chat_record_id: number;
  sql: string;
  data: { data: string[][]; fields: string[] };
  title: string;
  message?: string;
  success?: boolean;
}> => {
  if (USE_MOCK) {
    await delay(1200, signal);
    maybeFailMock();
    return MOCK_SQL_DATA;
  }
  return http.post(
    `${baseURL}/chat/question/sql_data${_path}`,
    { chat_id: chatId, question: question, token: token, biz_code: common.bizType, ...userInfo },
    { headers: commonHeaders(), signal },
  );
};

/**
 * 分段式请求，返回结果图表
 * @returns
 */
export const recordChat = async (
  chat_id: string,
  chat_record_id: string | number,
  token: string,
  signal?: AbortSignal,
): Promise<{
  success: boolean;
  record_id?: number;
  chat_record_id: number;
  chart: ChartData;
  data?: any[];
}> => {
  if (USE_MOCK) {
    await delay(1000, signal);
    maybeFailMock();
    return MOCK_RECORD_CHART;
  }
  return http.post(
    `${baseURL}/chat/${chat_id}/record/${chat_record_id}/chart${_path}`,
    { token: token, biz_code: common.bizType, ...userInfo },
    { headers: commonHeaders(), signal },
  );
};

/**
 * 总结数据（SSE 流式）
 * POST /api/v1/chat/{chat_id}/record/{chat_record_id}/analysis
 * 返回类 EventSource 对象，调用方负责管理
 * @param chat_id - 会话 ID
 * @param chat_record_id - 对话记录 ID
 * @param token - 访问令牌
 * @param signal - 取消信号
 * @returns 类 EventSource 对象，onmessage 事件获取追加内容
 */
export const summarizeData = async (
  chat_id: string,
  chat_record_id: string | number,
  token: string,
  signal?: AbortSignal,
): Promise<EventSource> => {
  if (USE_MOCK) {
    // 模拟 SSE 流式输出
    await delay(1000, signal);
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    const POLL_INTERVAL = 100; // 模拟轮询间隔
    let timer: ReturnType<typeof setInterval> | undefined;
    const mockEventSource = {
      onmessage: null as ((this: EventSource, ev: MessageEvent) => unknown) | null,
      onerror: null as ((this: EventSource, ev: Event) => unknown) | null,
      close: () => {
        if (timer) {
          clearInterval(timer);
        }
      },
      dispatchEvent: (event: Event) => {
        if (event.type === 'message') {
          mockEventSource.onmessage?.call(mockEventSource as unknown as EventSource, event as MessageEvent);
        }
        return true;
      },
    } as EventSource;

    signal?.addEventListener('abort', () => mockEventSource.close(), { once: true });
    const content = MOCK_SUMMARIZE.content || '这是一个模拟总结结果...';
    const allCounts = Math.ceil(content.length / 10); // 每次发送 10 个字符
    let pollCount = 0;

    timer = setInterval(() => {
      pollCount++;
      mockEventSource.dispatchEvent(new MessageEvent('message', {
        data: JSON.stringify({
          type: 'text',
          text: content.slice((pollCount - 1) * 10, pollCount * 10),
          done: pollCount >= allCounts,
        })
      }));
      if (pollCount >= allCounts) {
        clearInterval(timer);
        mockEventSource.close();
      }
    }, POLL_INTERVAL);

    return mockEventSource;
  }

  const url = `${baseURL}/chat/${chat_id}/record/${chat_record_id}/analysis${_path}`;
  const headers = commonHeaders();
  const params = { token: token, biz_code: common.bizType, stream: true, ...userInfo };

  // 定义事件处理器映射
  const eventHandlers: SSEEventHandlers = {
    'content': (data) => {
      return {
        type: 'message',
        data,
      }
    },
    'error': (data) => ({
      type: 'error',
      data,
    }),
    'result': () => ({
      type: 'message',
      data: JSON.stringify({ done: true }),
      done: true,
    }),
  };

  return createSSEEventSource(url, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: params,
    eventHandlers,
    signal,
  });
};

// ===== Business Analysis Flow (新增) =====

/**
 * 发起分析请求
 * POST /api/v1/agent/resolve_async
 *
 * 响应格式：
 *   { task_id, status, raw: { taskId, sessionId, requestId, status, pollUrl } }
 * sessionId 由后端生成并返回，前端不再预先创建。
 */
export const analysisAsync = async (
  question: string,
  sessionId?: string | null,
  signal?: AbortSignal,
): Promise<{ taskId: string; sessionId: string; turnId: string; status: string }> => {
  if (USE_MOCK) {
    await delay(500, signal);
    const mockTaskId = `task_${Date.now()}`;
    return { taskId: mockTaskId, sessionId: mockTaskId, turnId: mockTaskId, status: 'queued' };
  }
  const res = await http.post<{
    task_id: string;
    status: string;
    raw: { taskId: string; sessionId: string; turnId: string; requestId: string; status: string; pollUrl: string };
  }>(`${baseURL}/agent/resolve_async`, {
    tenant_code: common.tenantCode ?? '',
    ...solutionRoleFields(),
    biz_code: common.bizType ?? '',
    uid: getIdentityUid(),
    payload: {
      sessionId,
      userPrompt: question,
      extraSession: buildExtraSession(),
    },
  },
    { headers: commonHeaders(), signal },
  );
  return {
    taskId: res.task_id,
    turnId: res.raw.turnId,
    sessionId: res.raw.sessionId,
    status: res.raw.status,
  };
};

/**
 * 轮询分析任务状态
 * GET /api/v1/agent/task?task_id=
 *
 * 响应格式：
 *   { task_id, status, raw: { ... } }
 * status 由后端归一化（queued / running / succeeded / failed 等）
 */
export const analysisStatus = async ({ taskId, sessionId, signal, turnId }: {
  taskId: string,
  sessionId?: string | null,
  signal?: AbortSignal,
  turnId?: string | null,
}): Promise<{
  status: TAnalysisStatus;
  hasReport: boolean;
  current_task_desc?: string;
  progressHistory?: IProgressHistoryItem[],
}> => {
  if (USE_MOCK) {
    await delay(300, signal);
    return { status: "succeeded", hasReport: true, current_task_desc: '分析中，请稍后...' };
  }
  const res = await http.get<{
    task_id: string;
    status: TAnalysisStatus;
    raw: Record<string, any>;
  }>(`${baseURL}/agent/task`, {
    params: {
      task_id: taskId,
      tenant_code: common.tenantCode ?? '',
      ...solutionRoleFields(),
      biz_code: common.bizType ?? '',
      store_id: common.storeId ?? '',
      org_id: common.orgId ?? '',
      sessionId,
      turnId,
    },
    headers: commonHeaders(),
    signal,
  });
  return {
    status: res.status,
    hasReport: !!res.raw?.hasReport,
    current_task_desc: res.raw?.currentTaskDesc || '',
    progressHistory: res.raw?.progressHistory || [],
  };
};

/**
 * 取消分析任务
 * POST /api/v1/agent/task/cancel?task_id=
 */
export const analysisCancelTask = async (
  taskId: string,
  sessionId?: string | null,
  signal?: AbortSignal,
): Promise<{ success: boolean }> => {
  if (USE_MOCK) {
    await delay(200, signal);
    return { success: true };
  }

  return http.post(
    `${baseURL}/agent/task/cancel`,
    undefined,
    {
      params: {
        task_id: taskId,
        tenant_code: common.tenantCode ?? '',
        ...solutionRoleFields(),
        biz_code: common.bizType ?? '',
        store_id: common.storeId ?? '',
        org_id: common.orgId ?? '',
        sessionId,
      },
      headers: commonHeaders(),
      signal,
    },
  );
};

/**
 * 获取分析报告（SSE 流式）
 * GET /api/v1/analysis/report
 * 使用 @microsoft/fetch-event-source 库实现 SSE 推流，支持自定义 headers
 * 返回类 EventSource 对象，调用方负责管理
 */
export const analysisReport = async ({ taskId, sessionId, turnId, signal }: {
  taskId: string,
  turnId?: string | null,
  sessionId?: string | null,
  signal?: AbortSignal,
}): Promise<EventSource> => {
  if (USE_MOCK) {
    // 模拟 SSE 流式输出
    await delay(3000, signal);
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    const POLL_INTERVAL = 50; // 模拟轮询间隔
    let timer: ReturnType<typeof setInterval> | undefined;
    const mockEventSource = {
      onmessage: null as ((this: EventSource, ev: MessageEvent) => unknown) | null,
      onerror: null as ((this: EventSource, ev: Event) => unknown) | null,
      close: () => {
        if (timer) {
          clearInterval(timer);
        }
      },
      dispatchEvent: (event: Event) => {
        if (event.type === 'message') {
          mockEventSource.onmessage?.call(mockEventSource as unknown as EventSource, event as MessageEvent);
        }
        return true;
      },
    } as EventSource;
    signal?.addEventListener('abort', () => {
      mockEventSource.close();
      // abort 时主动触发 onerror，确保调用方能正确处理取消状态
      mockEventSource.dispatchEvent(new Event('error'));
    }, { once: true });
    const content = MOCK_REPORT.content
    const allCounts = 600; // 模拟 600 条消息后完成
    let pollCount = 0;
    timer = setInterval(() => {
      pollCount++;
      mockEventSource.dispatchEvent(new MessageEvent('message', {
        data: JSON.stringify({
          type: 'text',
          text: content.slice((pollCount - 1) * 10, pollCount * 10),
          done: pollCount >= allCounts,
        })
      }));
      if (pollCount >= allCounts) {
        clearInterval(timer);
        mockEventSource.close();
      }
    }, POLL_INTERVAL);

    return mockEventSource;
  }

  const params = new URLSearchParams({
    tenant_code: common.tenantCode ?? '',
    ...solutionRoleFields(),
    biz_code: common.bizType ?? '',
    store_id: common.storeId ?? '',
    org_id: common.orgId ?? '',
    task_id: taskId ?? '',
    turnId: turnId ?? '',
    stream: 'true',
  });

  if (taskId) {
    params.set('task_id', taskId);
  }

  const url = `${baseURL}/agent/report?${params.toString()}`;
  const headers = commonHeaders();

  // 定义事件处理器映射
  const eventHandlers: SSEEventHandlers = {
    'biz.report.delta': (data) => ({
      type: 'message',
      data,
    }),
    'biz.report.error': (data) => ({
      type: 'error',
      data,
    }),
    'biz.report.done': (data) => {
      let parsedData = {}
      try {
        parsedData = JSON.parse(data);
      } catch (error) {
        console.error('Failed to parse report done data:', error);
      }
      return {
        type: 'message',
        data: JSON.stringify({ done: true, ...parsedData }),
      };
    },
  };

  return createSSEEventSource(url, {
    headers,
    eventHandlers,
    signal,
  });
};
